import React, { useState, useRef, useEffect } from 'react';

interface Evidence {
  id: string;
  title: string;
  file_name: string;
  snippet: string;
  conflicting_text?: string;
  page?: string | number;
  label: string;
  downloadable_url?: string;
}

interface EvidenceCardProps {
  evidence: Evidence;
  onLabelChange?: (label: string) => void;
}

export interface ClassificationOption {
  code: string;
  label: string;
  colorClass: string;
  borderColor: string;
}

export const CLASSIFICATION_OPTIONS: ClassificationOption[] = [
  { code: 'APPROVED_VERSION', label: 'Approved version', colorClass: 'cg-color-approved', borderColor: '#16a34a' },
  { code: 'TO_BE_CORRECTED', label: 'To be corrected', colorClass: 'cg-color-corrected', borderColor: '#ef4444' },
  { code: 'INTENTIONAL_DIFFERENCE', label: 'Intentional difference', colorClass: 'cg-color-intentional', borderColor: '#4f46e5' },
  { code: 'LITERATURE', label: 'Literature', colorClass: 'cg-color-literature', borderColor: '#0284c7' },
  { code: 'NOT_RELEVANT', label: 'Not relevant', colorClass: 'cg-color-irrelevant', borderColor: '#64748b' }
];

export const getClassificationOption = (val?: string): ClassificationOption | null => {
  if (!val) return null;
  const clean = val.trim();
  if (clean === 'NOT_SELECTED' || clean.toLowerCase() === 'choose appropriate') return null;
  return CLASSIFICATION_OPTIONS.find(
    opt => opt.code === clean.toUpperCase() || opt.label.toLowerCase() === clean.toLowerCase()
  ) || null;
};

const EvidenceCard: React.FC<EvidenceCardProps> = ({ evidence, onLabelChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(evidence.label || 'NOT_SELECTED');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelected(evidence.label || 'NOT_SELECTED');
  }, [evidence.label]);

  const selectedOpt = getClassificationOption(selected);
  const triggerLabel = selectedOpt ? selectedOpt.label : 'Choose appropriate';
  const triggerColorClass = selectedOpt ? selectedOpt.colorClass : 'cg-color-default';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const isUrlValid = (url?: string) => {
    if (!url) return false;
    if (url === "#" || url === "NULL" || url.toLowerCase() === "null") return false;
    return true;
  };

  const hasDownloadUrl = isUrlValid(evidence.downloadable_url);

  const getSourceDisplay = () => {
    if (!evidence.file_name || evidence.file_name === '-') return '-';
    const fileName = evidence.file_name;
    if (fileName.includes(', page')) {
      return fileName;
    }
    if (evidence.page) {
      return `${fileName} , page ${evidence.page}`;
    }
    return `${fileName} , page`;
  };

  const getConflictedTextAsTable = (source?: string) => {
    const raw = (source && source.trim()) || '-';
    if (!raw || !raw.trim() || raw === '-') return '-';

    const cleaned = raw.replace(/<\/?(?:table|thead|tbody)[^>]*>\s*/gi, '');

    const firstTR = cleaned.match(/<tr[^>]*>/i);
    let leading = '';
    if (firstTR) {
      leading = cleaned.slice(0, firstTR.index).replace(/\r?\n/g, '<br/>');
    } else return cleaned;

    const allClosingTR = [...cleaned.matchAll(/<\/tr>/gi)];
    const lastClosTR = allClosingTR.pop();
    let trailing = '';
    if (lastClosTR) {
      const afterLastEnd = (lastClosTR?.index ?? 0) + lastClosTR[0].length;
      if (afterLastEnd < cleaned.length) {
        trailing = cleaned.slice(afterLastEnd).replace(/\r?\n/g, '<br/>');
      }
    }

    const rowMatches = cleaned.match(/<tr[^>]*>[\s\S]*?<\/tr>/gi);

    if (rowMatches) {
      return leading + '<table class="cg-card-rendered-table"><tbody>' + rowMatches.join('').replace(/\r?\n/g, '<br/>') + '</tbody></table>' + trailing;
    }

    return raw;
  };

  return (
    <div className="cg-card">
      <div className="cg-card-label-dropdown" ref={dropdownRef}>
        <div
          className={`cg-dropdown-trigger ${triggerColorClass}`}
          onClick={() => setIsOpen(!isOpen)}
        >
          {triggerLabel}
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '4px' }}>
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>

        {isOpen && (
          <div className="cg-dropdown-menu">
            {CLASSIFICATION_OPTIONS.map((opt) => (
              <div
                key={opt.code}
                className={`cg-dropdown-item ${selectedOpt?.code === opt.code ? 'selected' : ''}`}
                onClick={() => {
                  setSelected(opt.code);
                  setIsOpen(false);
                  if (onLabelChange) {
                    onLabelChange(opt.code);
                  }
                }}
              >
                <div className="cg-dropdown-item-check">
                  {selectedOpt?.code === opt.code && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </div>
                <span className={`cg-dropdown-pill ${opt.colorClass}`}>{opt.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <br />
      <table className="cg-card-table">
        <tbody>
          <tr>
            <td className="cg-card-label">Conflicted text</td>
            <td className="cg-card-conflicted-value" title={evidence.title} dangerouslySetInnerHTML={{ __html: getConflictedTextAsTable(evidence.conflicting_text || evidence.snippet) }}></td>
          </tr>
          <tr>
            <td className="cg-card-label">Source</td>
            <td className="cg-card-source-value" title={evidence.file_name}>{getSourceDisplay()}</td>
          </tr>
          <tr>
            <td className="cg-card-label">Text snippet</td>
            <td className="cg-card-snippet-value" title={evidence.snippet} dangerouslySetInnerHTML={{ __html: getConflictedTextAsTable(evidence.snippet) }}></td>
          </tr>
        </tbody>
      </table>

      <a
        className="cg-card-expand"
        href={hasDownloadUrl ? evidence.downloadable_url : undefined}
        target={hasDownloadUrl ? "_blank" : undefined}
        rel={hasDownloadUrl ? "noopener noreferrer" : undefined}
        onClick={(e) => {
          if (!hasDownloadUrl) {
            e.preventDefault();
          }
        }}
        style={{
          opacity: hasDownloadUrl ? 1 : 0.5,
          cursor: hasDownloadUrl ? "pointer" : "not-allowed",
          display: 'inline-block'
        }}
        title={hasDownloadUrl ? "Open File" : "No downloadable file"}
      >
        <img src="/svg/expand.svg" alt="Expand" style={{ width: '18px', height: '18px' }} />
      </a>
    </div>
  );
};

export default EvidenceCard;
